<script>
import { mapState, mapGetters } from 'vuex';

import { formatAmount } from 'payment_page/lib/formatters';
import { PaymentMethods } from '../';

import CustomAgreement from './custom_agreement.vue';
import DiscountCode from './discount_code.vue';
import OrderQuantity from './order_quantity.vue';
import PaymentMethodAgreement from './payment_method_agreement.vue';
import RecurrenceMessage from './recurrence_message.vue';
import TotalAmount from './total_amount.vue';

export default {
  components: {
    CustomAgreement,
    DiscountCode,
    OrderQuantity,
    PaymentMethodAgreement,
    RecurrenceMessage,
    TotalAmount
  },

  data() {
    return {
      scope: {
        scope: 'pay.js.marketing.summary'
      }
    };
  },

  computed: {
    ...mapState([
      'product',
      'productRecurrence',
      'selectedPaymentType',
      'selectedPspCode'
    ]),

    ...mapState({
      orderBumpName: (state) => state.orderBump.name
    }),

    ...mapGetters([
      'hasDifferentPriceForFirstPeriod',
      'hasDifferentLengthForFirstPeriod',
      'isProductFreeForFirstPeriod',
      'orderQuantity',
      'payInInstallments',
      'selectedAmounts',
      'totalDiscountedAmount',
      'totalAmount',
      'getProductName'
    ]),

    formattedAmountExVat() {
      return formatAmount(this.selectedAmounts.product.amountExVat);
    },

    formattedVatAmount() {
      return formatAmount(this.selectedAmounts.vat.amount);
    },

    formattedTotalAmountIncVat() {
      return formatAmount(this.selectedAmounts.total.amountIncVat, true);
    },

    formattedTotalDiscountedAmountIncVat() {
      return formatAmount(this.selectedAmounts.discountedTotal.amountIncVat, true);
    },

    formattedOrderBumpAmount() {
      return formatAmount(this.selectedAmounts.orderBump.amountExVat);
    },

    showOrderBump() {
      return this.selectedAmounts.orderBump.amountExVat > 0;
    },

    showTotalDescription() {
      return this.payInInstallments || this.product.focusOnPriceExVat;
    },

    totalText() {
      if (!this.payInInstallments) {
        return this.t('total.total', this.scope);
      }

      if (this.productRecurrence.isSubscription) {
        if (this.hasDifferentPriceForFirstPeriod) {
          return this.t('total.first_period', this.scope);
        } else {
          return this.t('total.per_period', this.scope);
        }
      }

      if (this.hasDifferentPriceForFirstPeriod) {
        return this.t('total.first_installment', this.scope);
      } else {
        return this.t('total.per_installment', this.scope);
      }
    },

    isFirstProductAmountFree() {
      return this.payInInstallments && this.isProductFreeForFirstPeriod;
    },

    isDiscounted() {
      return this.totalAmount != this.totalDiscountedAmount;
    },

    isCustomAgreementRequired() {
      return this.product.documentText && this.product.documentLink;
    },

    isAgreementRequired() {
      return this.isCustomAgreementRequired || this.isPaymentMethodAgreementRequired;
    },

    isPaymentMethodAgreementRequired() {
      if (this.payInInstallments) {
        return this.selectedPspCode !== PaymentMethods.PAYPAL;
      }

      return [
        PaymentMethods.AFTERPAY,
        PaymentMethods.SEPA_DIRECT_DEBIT
      ].includes(this.selectedPspCode);
    }
  }
};
</script>

<template>
  <div class="c-summary">
    <h3>{{ t('title', scope) }}</h3>

    <div class="c-summary__row row">
      <div class="col d-flex align-items-center text-break">
        {{ getProductName }}
      </div>

      <div class="col-4">
        <order-quantity
          v-if="product.showOrderQuantity"
        />
      </div>

      <div class="col-3 d-flex align-items-center justify-content-end c-summary__amount">
        {{ isFirstProductAmountFree ? t('free', scope) : formattedAmountExVat }}
      </div>
    </div>

    <div
      v-if="showOrderBump"
      class="c-summary__row"
    >
      <div class="text-break">
        {{ orderBumpName }}
      </div>

      <div class="c-summary__amount">
        {{ formattedOrderBumpAmount }}
      </div>
    </div>

    <div
      v-if="!product.focusOnPriceExVat"
      class="c-summary__row"
    >
      <div class="text-break">
        {{ t('vat', scope) }}
      </div>

      <div class="c-summary__amount">
        {{ formattedVatAmount }}
      </div>
    </div>

    <discount-code
      v-if="product.showDiscount"
    />

    <div class="c-summary__row flex-wrap">
      <div class="c-summary__total c-summary__total__amount">
        {{ totalText }}

        <div class="c-summary__amount">
          <total-amount
            :payment-type="selectedPaymentType"
            :with-symbol="true"
          />
        </div>
      </div>

      <div
        v-if="showTotalDescription"
        class="c-summary__total mt-2"
      >
        <recurrence-message
          v-if="payInInstallments"
          type="short"
        />

        <div
          v-if="product.focusOnPriceExVat"
          class="ms-auto"
        >
          <small>
            {{ `${t('amount', scope)} ${t('inc_vat', scope)}` }}:
          </small>

          <small>
            {{ isDiscounted ? formattedTotalDiscountedAmountIncVat : formattedTotalAmountIncVat }}
          </small>
        </div>
      </div>

      <recurrence-message
        v-if="payInInstallments"
        type="detail"
        class="c-summary__total mt-2"
      />
    </div>

    <div
      v-if="isAgreementRequired"
      class="c-summary__row flex-column align-items-start"
    >
      <custom-agreement
        v-if="isCustomAgreementRequired"
        :document-text="product.documentText"
        :document-link="product.documentLink"
      />

      <payment-method-agreement
        v-if="isPaymentMethodAgreementRequired"
      />
    </div>
  </div>
</template>
